import { useQueryClient } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { FCC } from "types";

import { Sim } from "@/assets/icons/ds";
import { Button } from "@/components/Interface/Button/Button";
import { Notification } from "@/components/Interface/Notification/Notification";
import { CardModule } from "@/components/Layout/BaseLayouts/CardModule/CardModule";
import { BrandLoadingScreen } from "@/components/Layout/BrandLoadingScreen/BrandLoadingScreen";
import {
  SubscriptionInfoBox,
  SubscriptionInfoRow,
} from "@/components/Layout/SubscriptionInfoBox/SubscriptionInfoBox";
import { AutoRenewalBillPreview } from "@/components/Portal/DeviceManagement/components/AutoRenewalBillPreview";
import { useDisplayedSim } from "@/components/Portal/DeviceManagement/hooks/useDisplayedSim.ts";
import { useSubscriptionPriceBreakdown } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionPrice/useSubscriptionPriceBreakdown";
import { useAlert } from "@/hooks/useAlert";
import { useSetAutoRenew } from "@/services/api";
import { AlertTypes } from "@/utils/atoms";
import {
  getExtendedFlatNameFromLocalFlatSpeed,
  hasDifferentSuccessor,
} from "@/utils/deviceUtils";
import { getNextBillDate } from "@/utils/tariffUtils";

interface SubscriptionAutoRenewalProps {
  /**
   * Rendered inline (desktop) instead of as a full page (mobile): closing or
   * confirming should return to the status card in place, not navigate away.
   * Omitted when mounted as the route's own element (the mobile case).
   */
  onClose?: () => void;
}

export const SubscriptionAutoRenewal: FCC<SubscriptionAutoRenewalProps> = ({
  onClose,
}) => {
  const { t: tPrefixed } = useTranslation(undefined, {
    keyPrefix: "cockpit.managementTile.details.sections.autoRenewal",
  });
  const { t, i18n } = useTranslation("portal");
  const { t: tDefault } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showAlert = useAlert();

  const { data: simDetail, isPending: isLoading } = useDisplayedSim();

  const close = () =>
    onClose
      ? onClose()
      : navigate(`../display?simId=${simDetail?.simId}`, { replace: true });

  const { mutate: sendSetAutoRenew, isPending: isLoadingSetAutoRenew } =
    useSetAutoRenew({
      mutation: {
        onSuccess: () => {
          // The mutation itself already succeeded server-side by this point;
          // a rejected refetch must still close the panel and confirm it,
          // not leave the customer staring at a stale, already-submitted form.
          // Returned so the mutation stays pending until invalidation and
          // the close/alert both complete.
          return queryClient
            .invalidateQueries({ queryKey: ["/sim/sim"] })
            .finally(() => {
              close();
              showAlert({
                type: AlertTypes.success,
                text: copy.successMessage,
              });
            });
        },
      },
    });

  const changeAutoRenew = (newValue: boolean) => {
    if (simDetail?.simId) {
      sendSetAutoRenew({
        simId: simDetail?.simId,
        params: { autoRenew: newValue },
      });
    }
  };

  const autoRenewalActive = !!simDetail?.autoRenew;

  // Where the very next bill lands once auto-renewal kicks back in.
  const nextBillDate = getNextBillDate(simDetail, i18n.language);
  // Only the "enable" screen shows the price breakdown - skip the discount
  // and add-on computation on the "disable/pause" screen where it's unused.
  const priceBreakdown = useSubscriptionPriceBreakdown(
    autoRenewalActive ? undefined : simDetail,
  );

  // The screen turns auto-renewal off while it is on, and back on otherwise.
  const copy = autoRenewalActive
    ? {
        title: tPrefixed("disable.title"),
        successMessage: tPrefixed("disable.successMessage"),
        goodToKnow: tPrefixed("disable.goodToKnow"),
      }
    : {
        title: tPrefixed("enable.title"),
        successMessage: tDefault("portal:common.autoRenewal.successMessage"),
        goodToKnow: tDefault("portal:common.autoRenewal.goodToKnow"),
      };

  return !isLoading && simDetail ? (
    <CardModule
      dataTestid="subscription-auto-renewal"
      stickyHeader
      title={copy.title}
      actions={
        <>
          <Button
            dataTestid="cancel-button"
            size="sm"
            variant="secondary"
            onClick={close}
          >
            {tDefault("Common.label.cancel")}
          </Button>
          <Button
            dataTestid="success-button"
            size="sm"
            variant="primary"
            disabled={isLoadingSetAutoRenew}
            onClick={() => changeAutoRenew(!autoRenewalActive)}
          >
            {tPrefixed("buttons.confirm")}
          </Button>
        </>
      }
      bodyGap={2}
    >
      <p className="text-body-sm text-foreground-secondary">
        {autoRenewalActive ? (
          <Trans t={tPrefixed} values={{ count: simDetail.remainingDays ?? 0 }}>
            disable.description
          </Trans>
        ) : (
          <Trans
            t={tPrefixed}
            values={{ days: simDetail?.minContractDuration }}
          >
            enable.description
          </Trans>
        )}
      </p>

      {autoRenewalActive && hasDifferentSuccessor(simDetail) && (
        <p className="text-body-sm text-foreground-secondary">
          <Trans
            t={t}
            values={{
              flatName: getExtendedFlatNameFromLocalFlatSpeed(
                simDetail?.successorForRenew?.description,
              ),
            }}
            i18nKey="cockpit.managementTile.details.sections.autoRenewal.disable.successorWarning"
          />
        </p>
      )}

      <SubscriptionInfoBox>
        <SubscriptionInfoRow
          icon={<Sim className="size-6" />}
          title={getExtendedFlatNameFromLocalFlatSpeed(
            simDetail.localFlatSpeed,
          )}
        />
      </SubscriptionInfoBox>

      {!autoRenewalActive && (
        <AutoRenewalBillPreview
          title={tDefault("portal:common.autoRenewal.nextBillTitle")}
          description={tPrefixed("enable.nextBillDescription")}
          breakdown={priceBreakdown}
          minContractDuration={simDetail.minContractDuration}
          nextBillDate={nextBillDate}
        />
      )}

      <Notification
        title={tDefault("Content.goodToKnow")}
        description={copy.goodToKnow}
      />
    </CardModule>
  ) : (
    <BrandLoadingScreen />
  );
};
